import { inject } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { Router, RouterStateSnapshot } from '@angular/router';
import { AppolowModalRef, ModalDismissReasons } from '@appolow/modal';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { Toast, Translations } from '@core/services';
import { modal } from '@core/services/dialog.service';

/**
 * Interface for components that can be deactivated with unsaved form changes
 */
export interface CanComponentDeactivate {
	/** The form control to check for unsaved changes */
	form: AbstractControl;
	/** Optional method to perform additional cleanup before navigation */
	canDeactivate?(): boolean | Promise<boolean>;
}

/**
 * Configuration options for the form guard
 */
export interface FormGuardOptions {
	/** Enable debug logging (only in development) */
	debug?: boolean;
	/** Custom message for the confirmation dialog */
	customMessage?: string;
	/** Custom title for the confirmation dialog */
	customTitle?: string;
	/** Skip dirty check and always show confirmation */
	forceConfirmation?: boolean;
	/** Additional validation function */
	customValidator?: (form: AbstractControl) => boolean;
	/**
	 * Component instance tied to the guard. Required when the guard
	 * needs to resume navigation manually after user confirmation.
	 */
	componentRef?: CanComponentDeactivate;
	/** Router state representing the attempted navigation target */
	nextState?: RouterStateSnapshot | null;
}

/** Tracks components that already confirmed navigation to bypass the guard once */
const bypassGuardOnce = new WeakSet<CanComponentDeactivate>();
/** Prevents multiple confirmation modals per component at the same time */
const pendingConfirmation = new WeakSet<CanComponentDeactivate>();

/**
 * Helper function to check if a form or any of its nested forms is dirty
 * @param control - The form control to check recursively
 * @returns true if the form or any nested forms have unsaved changes
 */
function isFormDirtyRecursive(control: AbstractControl): boolean {
	if (control.dirty) {
		return true;
	}

	if (control instanceof FormGroup) {
		return Object.values(control.controls).some((child) =>
			isFormDirtyRecursive(child)
		);
	}

	if (control instanceof FormArray) {
		return control.controls.some((child) => isFormDirtyRecursive(child));
	}

	return false;
}

/**
 * Enhanced guard that prevents navigation when there are unsaved form changes
 *
 * Features:
 * - Recursive dirty checking for nested forms
 * - Customizable confirmation messages
 * - Debug logging in development
 * - Robust error handling
 * - Support for custom validation logic
 *
 * @param form - The form control to check for unsaved changes
 * @param options - Configuration options for the guard behavior
 * @returns A Promise<boolean> indicating if navigation is allowed
 *
 * @example
 * ```typescript
 * // Basic usage
 * canDeactivate: [
 *   (component, _currentRoute, _currentState, nextState) =>
 *     canDeactivateFormFn(component.form, {
 *       componentRef: component,
 *       nextState
 *     })
 * ]
 *
 * // With options
 * canDeactivate: [() => canDeactivateFormFn(component.form, {
 *   debug: true,
 *   customMessage: 'You have unsaved role changes',
 *   customValidator: (form) => form.get('name')?.dirty || false
 * })]
 * ```
 */
export const canDeactivateFormFn = async (
	form: AbstractControl | null | undefined,
	options: FormGuardOptions = {}
): Promise<boolean> => {
	const {
		debug = false,
		customMessage,
		customTitle,
		forceConfirmation = false,
		customValidator,
		componentRef,
		nextState
	} = options;

	if (modal().hasOpenModals()) {
		return false;
	}

	try {
		const router =
			componentRef && nextState ? inject(Router, { optional: true }) : null;

		if (componentRef && bypassGuardOnce.has(componentRef)) {
			bypassGuardOnce.delete(componentRef);
			return true;
		}

		if (componentRef && pendingConfirmation.has(componentRef)) {
			return false;
		}

		if (!form) {
			if (debug && !environment.production) {
				console.warn('[FormGuard] No form provided, allowing navigation');
			}
			return true;
		}

		// Check if form has unsaved changes
		let hasUnsavedChanges = forceConfirmation;

		if (!hasUnsavedChanges) {
			// Use custom validator if provided, otherwise use recursive dirty check
			hasUnsavedChanges = customValidator
				? customValidator(form)
				: isFormDirtyRecursive(form);
		}

		// Allow navigation if no unsaved changes
		if (!hasUnsavedChanges) {
			if (debug && !environment.production) {
				console.log('[FormGuard] No unsaved changes, allowing navigation');
			}
			return true;
		}

		if (debug && !environment.production) {
			console.log(
				'[FormGuard] Unsaved changes detected, showing confirmation dialog'
			);
		}

		// Show confirmation dialog with high z-index to appear above loading
		const modalRef: AppolowModalRef = modal().open(ConfirmDialogComponent, {
			windowClass: 'modal-with-side-background modal-above-loading',
			size: 'lg',
			animation: true,
			backdrop: 'static',
			centered: true,
			headerSelector: '.confirm-dialog__header',
			footerSelector: '.confirm-dialog__footer'
		});

		Object.assign(modalRef.componentInstance, {
			title: Translations.instant(
				customTitle || 'GENERIC.TITLES.UNSAVED_CHANGES'
			),
			content: Translations.instant(
				customMessage || 'GENERIC.MESSAGES.WARNING.UNSAVED_CHANGES_WARNING'
			),
			showDenyButton: true,
			confirmButtonText: Translations.instant('GENERIC.BUTTONS.LEAVE'),
			denyButtonText: Translations.instant('GENERIC.BUTTONS.STAY'),
			icon: 'warning'
		});

		const modalHidden = firstValueFrom(modalRef.hidden);

		const canHandleNavigation = !!(componentRef && router && nextState?.url);

		if (canHandleNavigation) {
			pendingConfirmation.add(componentRef);

			modalRef.result
				.then(async (userChoice) => {
					await modalHidden.catch(() => {});
					pendingConfirmation.delete(componentRef);

					if (debug && !environment.production) {
						console.log('[FormGuard] User choice:', userChoice);
					}

					if (!userChoice) {
						return;
					}

					try {
						bypassGuardOnce.add(componentRef);
						await router.navigateByUrl(nextState.url);
					} catch (navigationError) {
						bypassGuardOnce.delete(componentRef);
						console.error(
							'[FormGuard] Navigation after confirmation failed:',
							navigationError
						);
						Toast.error(
							Translations.instant('GENERIC.MESSAGES.ERROR.FORM_GUARD_ERROR')
						);
					}
				})
				.catch(async (error: unknown) => {
					await modalHidden.catch(() => {});
					pendingConfirmation.delete(componentRef);
					handleModalError(error, debug);
				});

			return false;
		}

		const userChoice = await modalRef.result;
		await modalHidden;

		if (debug && !environment.production) {
			console.log('[FormGuard] User choice:', userChoice);
		}

		return userChoice;
	} catch (error: unknown) {
		handleModalError(error, debug);
		return false;
	}
};

/**
 * Simplified version for components that implement CanComponentDeactivate
 * @param component - Component with a form property
 * @param options - Configuration options
 * @returns Promise<boolean> indicating if navigation is allowed
 */
export const canDeactivateComponent = async (
	component: CanComponentDeactivate,
	options: FormGuardOptions = {}
): Promise<boolean> => {
	// If component has its own canDeactivate method, use it
	if (component.canDeactivate) {
		const componentResult = await Promise.resolve(component.canDeactivate());
		if (!componentResult) {
			return false;
		}
	}

	// Check form state
	return canDeactivateFormFn(component.form, {
		...options,
		componentRef: component
	});
};

function handleModalError(error: unknown, debug: boolean): void {
	if (debug && !environment.production) {
		console.log('[FormGuard] Modal dismissed or error occurred:', error);
	}

	const isUserDismissal =
		error instanceof Error &&
		[ModalDismissReasons.ESC, ModalDismissReasons.BACKDROP_CLICK].includes(
			error.message as any
		);

	if (!isUserDismissal) {
		Toast.error(
			Translations.instant('GENERIC.MESSAGES.ERROR.FORM_GUARD_ERROR')
		);
		console.error('[FormGuard] Unexpected error:', error);
	}
}
